import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  PhotoIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ChangeOrderForm = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [changeOrder, setChangeOrder] = useState(null);
  const [serviceCalls, setServiceCalls] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    service_call_id: '',
    title: '',
    description: '',
    reason: '',
    original_scope: '',
    new_scope: '',
    original_price: '',
    new_price: '',
    price_difference: ''
  });

  useEffect(() => {
    fetchServiceCalls();
    if (id) {
      fetchChangeOrder();
    }
  }, [id]);

  const fetchServiceCalls = async () => {
    try {
      const response = await fetch(`${API_URL}/service-calls`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServiceCalls(data.serviceCalls || []);
      }
    } catch (error) {
      console.error('Error fetching service calls:', error);
    }
  };

  const fetchChangeOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/change-orders/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch change order');
      const data = await response.json();
      setChangeOrder(data);
      setFormData({
        service_call_id: data.service_call_id || '',
        title: data.title || '',
        description: data.description || '',
        reason: data.reason || '',
        original_scope: data.original_scope || '',
        new_scope: data.new_scope || '',
        original_price: data.original_price || '',
        new_price: data.new_price || '',
        price_difference: data.price_difference || ''
      });
      setPhotos(data.photos || []);
    } catch (error) {
      console.error('Error fetching change order:', error);
      toast.error('Failed to load change order');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate price difference
      if (name === 'original_price' || name === 'new_price') {
        const original = parseFloat(name === 'original_price' ? value : updated.original_price) || 0;
        const newPrice = parseFloat(name === 'new_price' ? value : updated.new_price) || 0;
        updated.price_difference = (newPrice - original).toFixed(2);
      }
      
      return updated;
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (!id) {
      toast.error('Please save the change order first before uploading photos');
      return;
    }

    setUploadingPhotos(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('photo', file);

        const response = await fetch(`${API_URL}/photos/change-order/${id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!response.ok) throw new Error('Upload failed');
        return await response.json();
      });

      const results = await Promise.all(uploadPromises);
      setPhotos(prev => [...prev, ...results.map(r => r.photo)]);
      toast.success(`${files.length} photo(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const response = await fetch(`${API_URL}/photos/change-order/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Delete failed');
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const method = id ? 'PUT' : 'POST';
      const url = id ? `${API_URL}/change-orders/${id}` : `${API_URL}/change-orders`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save change order');
      const data = await response.json();

      toast.success(id ? 'Change order updated successfully' : 'Change order created successfully');
      
      if (!id && data.changeOrder?.id) {
        navigate(`/change-orders/${data.changeOrder.id}`);
      } else {
        navigate('/change-orders');
      }
    } catch (error) {
      console.error('Error saving change order:', error);
      toast.error('Failed to save change order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={() => navigate('/change-orders')}
          style={{
            padding: '8px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex'
          }}
        >
          <ArrowLeftIcon style={{ width: 20, height: 20 }} />
        </button>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
            {id ? 'Edit Change Order' : 'New Change Order'}
          </h1>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Document scope and price changes</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Service Call Selection */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>Service Call</h2>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Select Service Call *</label>
              <select
                name="service_call_id"
                required
                value={formData.service_call_id}
                onChange={handleChange}
                disabled={!!id}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="">Choose a service call...</option>
                {serviceCalls.map(sc => (
                  <option key={sc.id} value={sc.id}>
                    {sc.title} - {sc.client_name} ({new Date(sc.scheduled_date).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Change Details */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>Change Details</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Title *</label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Additional Equipment Required"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Description *</label>
              <textarea
                name="description"
                required
                rows="4"
                value={formData.description}
                onChange={handleChange}
                placeholder="What changed..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Reason for Change</label>
              <textarea
                name="reason"
                rows="3"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Why is this change needed..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Scope Changes */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>Scope Comparison</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Original Scope</label>
                <textarea
                  name="original_scope"
                  rows="4"
                  value={formData.original_scope}
                  onChange={handleChange}
                  placeholder="What was originally planned..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>New Scope</label>
                <textarea
                  name="new_scope"
                  rows="4"
                  value={formData.new_scope}
                  onChange={handleChange}
                  placeholder="What will now be done..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
            </div>
          </div>

          {/* Price Adjustment */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <CalculatorIcon style={{ width: 24, height: 24, color: '#2B7FE0' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Price Adjustment</h2>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Original Price</label>
                <input
                  type="number"
                  name="original_price"
                  step="0.01"
                  value={formData.original_price}
                  onChange={handleChange}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>New Price</label>
                <input
                  type="number"
                  name="new_price"
                  step="0.01"
                  value={formData.new_price}
                  onChange={handleChange}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Difference</label>
                <div style={{
                  padding: '8px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: formData.price_difference >= 0 ? '#dc2626' : '#059669',
                  background: '#f9fafb'
                }}>
                  {formData.price_difference ? `${formData.price_difference >= 0 ? '+' : ''}$${parseFloat(formData.price_difference).toLocaleString()}` : '$0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Photos */}
          {id && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>Documentation Photos</h2>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: '#f3f4f6',
                  border: '2px dashed #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#4b5563'
                }}>
                  <CloudArrowUpIcon style={{ width: 20, height: 20 }} />
                  {uploadingPhotos ? 'Uploading...' : 'Upload Photos'}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhotos}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {photos.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px' }}>
                  {photos.map(photo => (
                    <div key={photo.id} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                      <img
                        src={photo.photo_url}
                        alt="Change Order"
                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(photo.id)}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          padding: '4px',
                          background: 'rgba(0,0,0,0.7)',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex'
                        }}
                      >
                        <XMarkIcon style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  <PhotoIcon style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#d1d5db' }} />
                  <p>No photos uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => navigate('/change-orders')}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#2B7FE0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : id ? 'Update Change Order' : 'Submit Change Order'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChangeOrderForm;